import { isIsoDate, iso, shiftDate, today as currentDay } from '../format/dates'
import type { Subscription } from '../types/account'
import type { SubscriptionAction, SubscriptionSnapshot } from '../types/requests'
import type { Policy } from '../types/site'

/** De instellingen die de abonnementsregels nodig hebben — de aanroeper geeft ze mee (meestal `policy.subscription`). */
export type SubscriptionRules = Pick<Policy['subscription'], 'defaultIntervalWeeks' | 'leadDays'>

const newId = () => `ab-${Date.now().toString(36)}`

/** "Schuif een week op": door klant (Mijn omgeving) en salon (beheer) gebruikt — één waarde. */
export const POSTPONE_DAYS = 7

/** Hoogste aantal per product in een abonnement. */
export const MAX_SUBSCRIPTION_QTY = 9
/** Grenzen voor invoer uit de API of het beheer. Daarbuiten is het een vergissing, geen keuze. */
export const MAX_INTERVAL_WEEKS = 26
export const MAX_PAUSE_WEEKS = 26
export const MAX_POSTPONE_DAYS = 60

/** Een heel getal binnen de grenzen, of `null` als de invoer geen eindig getal is. */
const wholeIn = (n: unknown, min: number, max: number): number | null =>
  typeof n === 'number' && Number.isFinite(n) ? Math.max(min, Math.min(max, Math.round(n))) : null

/**
 * Past één abonnementsactie toe. Puur en zonder bijwerkingen: de site gebruikt
 * dit om de wijziging meteen te tonen (en in de demo als "server"); de backend
 * hoort exact dezelfde regels te volgen en is leidend (zie subscription.test.ts).
 *
 * Ongeldige invoer (NaN, 0 weken, een kapotte datum) wordt nooit doorgerekend:
 * de actie doet dan niets, of rekent vanaf vandaag in plaats van vanaf een datum die niet bestaat.
 */
export function applySubscriptionAction(
  sub: Subscription | null,
  action: SubscriptionAction,
  rules: SubscriptionRules,
  now = iso(new Date()),
): Subscription | null {
  const today = isIsoDate(now) ? now : iso(new Date())
  /** Het opgeslagen ritme; 0, negatief of NaN is kapotte data → het standaardritme. */
  const intervalOf = (s: Subscription | null) => {
    const w = wholeIn(s?.intervalWeeks, 0, MAX_INTERVAL_WEEKS)
    return w !== null && w >= 1 ? w : rules.defaultIntervalWeeks
  }
  /** De huidige leverdatum, of vandaag als die kapot is. */
  const nextOf = (s: Subscription) => (isIsoDate(s.nextDelivery) ? s.nextDelivery : today)
  /**
   * Een mislukte betaling hoort bij de eerstvolgende levering (F20). Schuift die levering,
   * dan schuift de melding mee; valt hij weg (overslaan, pauzeren, stoppen), dan vervalt
   * de openstaande betaling. De server doet hetzelfde en annuleert dan de Mollie-betaling.
   */
  const moveTo = (s: Subscription, nextDelivery: string): Subscription =>
    s.paymentIssue ? { ...s, nextDelivery, paymentIssue: { ...s.paymentIssue, deliveryDate: nextDelivery } } : { ...s, nextDelivery }
  const dropIssue = ({ paymentIssue: _dropped, ...s }: Subscription): Subscription => s

  if (action.type === 'start') {
    const productIds = Array.from(new Set(action.productIds.filter((id) => typeof id === 'string' && id.trim() !== '')))
    // Nooit een leeg abonnement starten.
    if (!productIds.length) return sub
    return {
      id: sub?.id ?? newId(),
      status: 'actief',
      intervalWeeks: intervalOf(sub),
      nextDelivery: shiftDate(today, rules.leadDays),
      items: productIds.map((productId) => ({ productId, qty: 1 })),
      extras: [],
      deliveriesDone: sub?.deliveriesDone ?? 0,
    }
  }
  if (!sub) return sub

  switch (action.type) {
    case 'skip':
      if (sub.status === 'gestopt') return sub
      return { ...dropIssue(sub), nextDelivery: shiftDate(nextOf(sub), intervalOf(sub) * 7) }
    case 'postpone': {
      const days = wholeIn(action.days, 1, MAX_POSTPONE_DAYS)
      if (days === null || sub.status === 'gestopt') return sub
      return moveTo(sub, shiftDate(nextOf(sub), days))
    }
    case 'sendEarlier': {
      if (sub.status === 'gestopt') return sub
      // Morgen inpakken, overmorgen in huis. Staat de levering al eerder (maar niet in het verleden), dan blijft die staan:
      // "eerder" mag nooit later worden.
      const earliest = shiftDate(today, 2)
      const next = sub.nextDelivery
      const keep = isIsoDate(next) && next >= today && next <= earliest
      return moveTo(sub, keep ? next : earliest)
    }
    case 'pause': {
      const weeks = wholeIn(action.weeks, 1, MAX_PAUSE_WEEKS)
      if (weeks === null || sub.status === 'gestopt') return sub
      const until = shiftDate(today, weeks * 7)
      return { ...dropIssue(sub), status: 'gepauzeerd', pausedUntil: until, nextDelivery: shiftDate(until, 1) }
    }
    case 'resume': {
      const keep = sub.status === 'actief' && isIsoDate(sub.nextDelivery) && sub.nextDelivery >= today
      return {
        ...sub,
        status: 'actief',
        pausedUntil: undefined,
        nextDelivery: keep ? sub.nextDelivery : shiftDate(today, rules.leadDays),
      }
    }
    case 'cancel':
      return { ...dropIssue(sub), status: 'gestopt', extras: [] }
    case 'setQty': {
      const qty = wholeIn(action.qty, 1, MAX_SUBSCRIPTION_QTY)
      if (qty === null) return sub
      return { ...sub, items: sub.items.map((i) => (i.productId === action.productId ? { ...i, qty } : i)) }
    }
    case 'removeItem':
      // Nooit een leeg abonnement: het laatste product blijft staan (pauzeren of stoppen is de weg).
      return sub.items.length <= 1 ? sub : { ...sub, items: sub.items.filter((i) => i.productId !== action.productId) }
    case 'setInterval': {
      const weeks = wholeIn(action.weeks, 1, MAX_INTERVAL_WEEKS)
      return weeks === null ? sub : { ...sub, intervalWeeks: weeks }
    }
    case 'toggleExtra':
      return {
        ...sub,
        extras: sub.extras.includes(action.productId)
          ? sub.extras.filter((x) => x !== action.productId)
          : [...sub.extras, action.productId],
      }
  }
}

/**
 * Laatste dag waarop de volgende levering nog aan te passen is: `changeUntilDays` vóór de leverdag
 * (voorwaarden: "tot 2 dagen voor verzending"). Daarna pakt Sarah in.
 */
export const changeDeadline = (nextDelivery: string, rules: Pick<Policy['subscription'], 'changeUntilDays'>) =>
  shiftDate(nextDelivery, -Math.max(0, rules.changeUntilDays))

/**
 * Mag de klant deze levering nog wijzigen (aantallen, overslaan, uitstellen, extra's)?
 * Pauzeren en stoppen vallen hier niet onder: die gaan over de levering daarna.
 * Een kapotte datum telt als "ja", zodat de klant nooit vastzit; de server beslist.
 * De server moet dit zelf afdwingen; dit is alleen wat de klant te zien krijgt.
 */
export const canChangeDelivery = (
  nextDelivery: string,
  rules: Pick<Policy['subscription'], 'changeUntilDays'>,
  todayIso: string = currentDay(),
) => !isIsoDate(nextDelivery) || todayIso <= changeDeadline(nextDelivery, rules)

/**
 * De stand die "ongedaan maken" terugzet. ALLEEN voor de server en de demo: die bewaren hem
 * bij elke actie onder een `undoToken`. De site stuurt nooit zelf een snapshot mee.
 */
export const snapshotOf = (s: Subscription): SubscriptionSnapshot => ({
  status: s.status,
  nextDelivery: s.nextDelivery,
  intervalWeeks: s.intervalWeeks,
  items: s.items.map((i) => ({ ...i })),
  extras: [...s.extras],
  pausedUntil: s.pausedUntil,
})

/** Zet een bewaarde stand terug, inclusief een pauzedatum die er toen niet was. */
export const restoreSnapshot = (s: Subscription, snap: SubscriptionSnapshot): Subscription => ({
  ...s,
  ...snap,
  pausedUntil: snap.pausedUntil,
  items: snap.items.map((i) => ({ ...i })),
  extras: [...snap.extras],
})
